import {
	axiosInstance,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { getTableRows } from "@/shared/utils/storedProcedureRows";
import { get } from "lodash-es";
import moment from "moment";

import {
	SECTION_SPEC_MEDIA_TYPE,
	type SectionSpecUpload,
} from "../models/SectionSpecUpload";
import {
	type SectionSpec,
	SECTION_SPEC_CONFIRMATION_ACTION,
	type SectionSpecConfirmationAction,
	type SectionSpecConfirmationResult,
	type SectionSpecDetail,
	type SectionSpecFileKind,
	type SectionSpecItem,
	type SectionSpecMetadata,
	type SectionSpecPublication,
	type SectionSpecVersion,
	type SpecItemPayload,
	WEIGHT_ITEM_CODE,
} from "../models/SectionSpec";

const isPositiveInteger = (value: unknown): value is number =>
	typeof value === "number" && Number.isInteger(value) && value > 0;

const parseSectionSpecUploadResponse = (
	response: unknown,
): SectionSpecUpload => {
	if (!isRecord(response) || response.success !== true) {
		throw new Error("The upload service did not report a successful upload");
	}

	if (!Array.isArray(response.result) || response.result.length !== 1) {
		throw new Error(
			"The upload service did not return exactly one file record",
		);
	}

	const fileRecord: unknown = response.result[0];
	if (!isRecord(fileRecord)) {
		throw new Error("The upload service returned an invalid file record");
	}

	const uploadFileId = fileRecord.UploadFileId;
	if (!isPositiveInteger(uploadFileId)) {
		throw new Error("The upload service returned an invalid UploadFileId");
	}

	const fileName = fileRecord.FileName;
	if (typeof fileName !== "string" || fileName.trim() === "") {
		throw new Error("The upload service returned an invalid FileName");
	}

	return { uploadFileId, fileName };
};

interface GetSectionSpecParams {
	sectionId?: number | null;
	assetId?: number | null;
	profileSpecId?: number | null;
	inspectedAt?: Date | null;
}

interface UpsertSectionSpecParams {
	profileSpecId?: number | null;
	sectionId: number;
	assetId: number;
	printNo?: string | null;
	area?: number | null;
	targetLbsPerFoot?: number | null;
	minLbsPerFoot?: number | null;
	maxLbsPerFoot?: number | null;
	weightProfileItemId: number | null;
	legDeltaMax?: number | null;
	millDrawingUploadFileId?: number | null;
	dimensionsReferenceUploadFileId?: number | null;
	comments?: string | null;
	items: SpecItemPayload[];
}

interface CorrectSectionSpecParams {
	profileSpecId: number;
	rev: number | null;
	effectiveDate: string | null;
	printNo: string | null;
	area: number | null;
	targetLbsPerFoot: number | null;
	minLbsPerFoot: number | null;
	maxLbsPerFoot: number | null;
	weightProfileItemId: number | null;
	legDeltaMax: number | null;
	millDrawingUploadFileId: number | null;
	dimensionsReferenceUploadFileId: number | null;
	comments: string | null;
	items: SpecItemPayload[];
}

const DOWNLOAD_CONTENT_TYPE = "application/octet-stream";
const FILE_EXTENSION = /\.([^.]+)$/;
const PREVIEW_MEDIA_TYPES = new Map<string, string>([
	["avif", "image/avif"],
	["bmp", "image/bmp"],
	["gif", "image/gif"],
	["jpeg", "image/jpeg"],
	["jpg", "image/jpeg"],
	["pdf", SECTION_SPEC_MEDIA_TYPE.PDF],
	["png", "image/png"],
	["svg", "image/svg+xml"],
	["webp", "image/webp"],
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const isConfirmationAction = (
	value: unknown,
): value is SectionSpecConfirmationAction =>
	value === SECTION_SPEC_CONFIRMATION_ACTION.IMAGES_UPDATED ||
	value === SECTION_SPEC_CONFIRMATION_ACTION.REVISION_CONFIRMED;

const isPositiveSafeInteger = (value: unknown): value is number =>
	typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const isNonNegativeSafeInteger = (value: unknown): value is number =>
	typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

const parseSectionSpecConfirmationResult = (
	response: unknown,
): SectionSpecConfirmationResult => {
	const result: unknown = getTableRows<unknown>(response, 0)[0];
	if (result === undefined) {
		throw new Error("The confirmation service did not return a result row");
	}
	if (
		!isRecord(result) ||
		!isConfirmationAction(result.ActionCode) ||
		!isPositiveSafeInteger(result.ProfileSpecId) ||
		!isPositiveSafeInteger(result.Rev) ||
		!isNonNegativeSafeInteger(result.SubRev)
	) {
		throw new Error("The confirmation service returned an invalid result");
	}

	return {
		ActionCode: result.ActionCode,
		ProfileSpecId: result.ProfileSpecId,
		Rev: result.Rev,
		SubRev: result.SubRev,
	};
};

interface SectionSpecMetadataResponse
	extends Omit<
		SectionSpecMetadata,
		"PreviousEffectiveDate" | "LatestInspectedAt"
	> {
	PreviousEffectiveDate?: string | null;
	LatestInspectedAt?: string | null;
}

const parseNullableUtcDate = (value: string | null | undefined): Date | null =>
	value == null ? null : moment.utc(value).toDate();

type SectionSpecResponse = Omit<
	SectionSpec,
	"TargetLbsPerFoot" | "MinLbsPerFoot" | "MaxLbsPerFoot"
>;

interface WeightBand {
	weightProfileItemId: number | null;
	targetLbsPerFoot?: number | null;
	minLbsPerFoot?: number | null;
	maxLbsPerFoot?: number | null;
}

const withWeightItem = (
	items: SpecItemPayload[],
	weight: WeightBand,
): SpecItemPayload[] => {
	const nominal = weight.targetLbsPerFoot ?? null;
	const ltl = weight.minLbsPerFoot ?? null;
	const utl = weight.maxLbsPerFoot ?? null;
	if (nominal === null && ltl === null && utl === null) return items;
	if (weight.weightProfileItemId === null)
		throw new Error("The WEIGHT item is missing for this mill");
	return [
		...items,
		{
			ProfileItemId: weight.weightProfileItemId,
			Ltl: ltl,
			Nominal: nominal,
			Utl: utl,
		},
	];
};

export const getSectionSpec = async (
	params: GetSectionSpecParams,
	signal?: AbortSignal,
): Promise<SectionSpecDetail> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId ?? null },
		{ name: "AssetId", value: params.assetId ?? null },
		{ name: "ProfileSpecId", value: params.profileSpecId ?? null },
		{ name: "InspectedAt", value: params.inspectedAt?.toISOString() ?? null },
	];

	const resp = await apiService.callV2(
		"[DIM].[GetProfileSpec]",
		parameters,
		signal,
	);

	if (!resp.ok) throw new Error(resp.message || "Error fetching section spec");

	const metadata = getTableRows<SectionSpecMetadataResponse>(resp, 2)[0];
	const spec = getTableRows<SectionSpecResponse>(resp, 0)[0];
	const items = getTableRows<SectionSpecItem>(resp, 1);
	const weight = items.find((item) => item.Code === WEIGHT_ITEM_CODE);
	return {
		spec: spec
			? {
					...spec,
					TargetLbsPerFoot: weight?.Nominal ?? null,
					MinLbsPerFoot: weight?.Ltl ?? null,
					MaxLbsPerFoot: weight?.Utl ?? null,
				}
			: null,
		items: items.filter((item) => item !== weight),
		weightProfileItemId: weight?.ProfileItemId ?? null,
		metadata: metadata
			? {
					...metadata,
					PreviousEffectiveDate: parseNullableUtcDate(
						metadata.PreviousEffectiveDate,
					),
					LatestInspectedAt: parseNullableUtcDate(metadata.LatestInspectedAt),
				}
			: null,
		versions: getTableRows<SectionSpecVersion>(resp, 3),
	};
};

export const upsertSectionSpec = async (params: UpsertSectionSpecParams) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ProfileSpecId", value: params.profileSpecId ?? null },
		{ name: "SectionId", value: params.sectionId },
		{ name: "AssetId", value: params.assetId },
		{ name: "PrintNo", value: params.printNo ?? null },
		{ name: "Area", value: params.area ?? null },
		{ name: "LegDeltaMax", value: params.legDeltaMax ?? null },
		{
			name: "MillDrawingUploadFileId",
			value: params.millDrawingUploadFileId ?? null,
		},
		{
			name: "DimensionsReferenceUploadFileId",
			value: params.dimensionsReferenceUploadFileId ?? null,
		},
		{ name: "Comments", value: params.comments ?? null },
		{
			name: "ItemsJson",
			value: JSON.stringify(withWeightItem(params.items, params)),
		},
	];

	const resp = await apiService.callV2("[DIM].[UpsertProfileSpec]", parameters);

	if (!resp.ok)
		throw new Error(resp.message || "Error saving the section spec");

	return get(resp, "data.returnValue", null) as number;
};

export const correctSectionSpec = async (params: CorrectSectionSpecParams) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ProfileSpecId", value: params.profileSpecId },
		{ name: "Rev", value: params.rev },
		{ name: "EffectiveDate", value: params.effectiveDate },
		{ name: "PrintNo", value: params.printNo },
		{ name: "Area", value: params.area },
		{ name: "LegDeltaMax", value: params.legDeltaMax },
		{ name: "MillDrawingUploadFileId", value: params.millDrawingUploadFileId },
		{
			name: "DimensionsReferenceUploadFileId",
			value: params.dimensionsReferenceUploadFileId,
		},
		{ name: "Comments", value: params.comments },
		{
			name: "ItemsJson",
			value: JSON.stringify(withWeightItem(params.items, params)),
		},
	];

	const resp = await apiService.callV2(
		"[DIM].[CorrectProfileSpec]",
		parameters,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error saving the published revision");

	return get(resp, "data.returnValue", null) as number;
};

export const replaceSectionSpecFile = async (params: {
	profileSpecId: number;
	fileKind: SectionSpecFileKind;
	uploadFileId: number;
	expectedUploadFileId: number | null;
	rotationAngle: number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "ProfileSpecId", value: params.profileSpecId },
		{ name: "FileKind", value: params.fileKind },
		{ name: "UploadFileId", value: params.uploadFileId },
		{ name: "ExpectedUploadFileId", value: params.expectedUploadFileId },
		{ name: "RotationAngle", value: params.rotationAngle },
	];

	const resp = await apiService.callV2(
		"[DIM].[ReplaceProfileSpecFile]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error replacing the file");
	}
};

export const uploadSectionSpecImage = async (
	file: File,
): Promise<SectionSpecUpload> => {
	const apiService = new MESApiService();
	const response: unknown = await apiService.uploadFiles(
		[file],
		"section-specs",
	);
	return parseSectionSpecUploadResponse(response);
};

export const downloadUploadFile = async (
	uploadFileId: number,
	signal?: AbortSignal,
): Promise<Blob> => {
	if (!Number.isInteger(uploadFileId) || uploadFileId <= 0) {
		throw new Error("A valid UploadFileId is required");
	}
	const response = await axiosInstance.get<Blob>(
		`/backend/files/${uploadFileId}/download`,
		{ responseType: "blob", signal },
	);
	return response.data;
};

export const downloadSectionSpecDrawing = async (
	uploadFileId: number,
	fileName: string | null,
	signal?: AbortSignal,
): Promise<Blob> => {
	const blob = await downloadUploadFile(uploadFileId, signal);
	const mediaType = PREVIEW_MEDIA_TYPES.get(
		fileName?.match(FILE_EXTENSION)?.[1].toLowerCase() ?? "",
	);

	// The download endpoint uses octet-stream; change metadata, not bytes.
	return blob.type === DOWNLOAD_CONTENT_TYPE && mediaType
		? blob.slice(0, blob.size, mediaType)
		: blob;
};

export const deleteSectionSpec = async (profileSpecId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[DIM].[DeleteProfileSpec]", [
		{ name: "ProfileSpecId", value: profileSpecId },
	]);

	if (!resp.ok) throw new Error(resp.message || "Error deleting the draft");
	return resp;
};

export const publishSectionSpec = async (
	profileSpecId: number,
	publication: SectionSpecPublication,
) => {
	const apiService = new MESApiService();

	const parameters = createSectionSpecPublicationParameters(
		profileSpecId,
		publication,
	);

	const resp = await apiService.callV2(
		"[DIM].[ConfirmProfileSpec]",
		parameters,
	);

	if (!resp.ok)
		throw new Error(resp.message || "Error confirming the revision");
	return parseSectionSpecConfirmationResult(resp);
};

const createSectionSpecPublicationParameters = (
	profileSpecId: number,
	publication: SectionSpecPublication,
): spParameter[] => {
	const parameters: spParameter[] = [
		{ name: "ProfileSpecId", value: profileSpecId },
		{ name: "EffectiveDate", value: publication.effectiveDate.toISOString() },
	];
	if (publication.revision !== undefined) {
		parameters.push({ name: "Rev", value: publication.revision });
	}
	return parameters;
};

export const unscheduleSectionSpec = async (profileSpecId: number) => {
	const apiService = new MESApiService();
	const resp = await apiService.callV2("[DIM].[UnscheduleProfileSpec]", [
		{ name: "ProfileSpecId", value: profileSpecId },
	]);

	if (!resp.ok)
		throw new Error(resp.message || "Error returning the revision to Draft");
	return get(resp, "data.returnValue", null) as number;
};
